import { useEffect, useRef, useState } from 'react'
import { AlertTriangle, ClipboardList, FileText, ShieldAlert, UserRound, Camera, Upload, Trash2, MessageSquareText, Pencil, Users, Mail, Printer, FileStack } from 'lucide-react'
import api from '../services/api'
import LookupSelect from '../components/LookupSelect'
import OcorrenciaTextoModal, { ChaveOcorrencia } from '../components/OcorrenciaTextoModal'
import OcorrenciaRelatoModal from '../components/OcorrenciaRelatoModal'
import OcorrenciaEnvolvidosModal from '../components/OcorrenciaEnvolvidosModal'
import OcorrenciaEmailModal from '../components/OcorrenciaEmailModal'
import { reduzirImagem } from '../utils/reduzirImagem'
import { useEmpresa } from '../contexts/EmpresaContext'

// Lançamento de Ocorrências dos Funcionários (TMOTOCO do ERP, frota.ocomot)

const NATUREZAS: [string, string][] = [
  ['A', 'Advertência'], ['S', 'Suspensão'], ['M', 'Multa'], ['T', 'Atestado'], ['F', 'Falta'],
  ['L', 'Folga'], ['H', 'Folga BCO'], ['I', 'Sinistro'], ['N', 'Afastamento'], ['O', 'Outros'],
]
const nomeNatureza = (n: string) => NATUREZAS.find(x => x[0] === n)?.[1] ?? (n || '—')
// campos liberados por natureza (igual ao ERP)
const comVeiculo = (n: string) => ['M', 'I', 'O'].includes(n)
const comMulta = (n: string) => ['M', 'I'].includes(n)
const comDias = (n: string) => ['S', 'T', 'F', 'H'].includes(n)

interface Linha { ococod: string; ocodat: string; ocotip: number; oconro: number; nome: string; tipo: string; oconat: string; ocosta: string; ocovei: string; ocoval: number; ocodia: number; ocousu: string }
interface Ocorrencia {
  ococod: string; ocodat: string; ocotip: number; oconro: number; nome: string; motpra?: number; tipo?: string
  oconat: string; ocosta: string; ocores: string; ocopon: number; ocoacu: number; ocovei: string; ocomul: string; ocoval: number
  ocopgt: string; ocodia: number; ocots1: string; ts1nome: string; ocots2: string; ts2nome: string; ocoobs: string
  ocodel: string; ocovnm: string; ocorsc: string; ocovte: string; ocohor: number; ococus: number; ocousu: string
  fotos?: string
}

type TipoFoto = 'L' | 'V'
const TIPOS_FOTO: [TipoFoto, string][] = [['L', 'Foto do Local'], ['V', 'Foto Veic. Empresa']]


const br = (iso?: string) => (iso ? iso.split('-').reverse().join('/') : '')
const isoLocal = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
const moeda = (v: number) => v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const horaParaInput = (h: number) => (h ? `${String(Math.trunc(h)).padStart(2, '0')}:${String(Math.round((h - Math.trunc(h)) * 100)).padStart(2, '0')}` : '')
const inputParaHora = (s: string) => { const [h, m] = s.split(':').map(Number); return s ? h + (m || 0) / 100 : 0 }
const somaDias = (iso: string, dias: number) => { const d = new Date(iso + 'T12:00:00'); d.setDate(d.getDate() + dias); return isoLocal(d) }

const novo = (): Ocorrencia => ({
  ococod: '', ocodat: isoLocal(new Date()), ocotip: 0, oconro: 0, nome: '', oconat: 'A', ocosta: 'A', ocores: '', ocopon: 0, ocoacu: 0,
  ocovei: '', ocomul: '', ocoval: 0, ocopgt: '', ocodia: 0, ocots1: '', ts1nome: '', ocots2: '', ts2nome: '', ocoobs: '',
  ocodel: '', ocovnm: '', ocorsc: '', ocovte: '', ocohor: 0, ococus: 0, ocousu: '',
})

function Lista({ onNovo, onEditar }: { onNovo: () => void; onEditar: (l: Linha) => void }) {
  const hoje = new Date()
  const [inicio, setInicio] = useState(isoLocal(new Date(hoje.getFullYear(), hoje.getMonth() - 2, 1)))
  const [fim, setFim] = useState(isoLocal(hoje))
  const [funcCod, setFuncCod] = useState('')
  const [funcNome, setFuncNome] = useState('')
  const [natureza, setNatureza] = useState('')
  const [status, setStatus] = useState('')
  const [lista, setLista] = useState<Linha[] | null>(null)
  const [erro, setErro] = useState('')

  async function buscar() {
    setErro('')
    try {
      const r = await api.get('/ocorrencias-funcionarios', { params: { inicio, fim, motcod: funcCod, natureza, status } })
      setLista(r.data)
    } catch (e: any) {
      setErro(e.response?.data?.error ?? 'Erro ao buscar as ocorrências.')
    }
  }
  useEffect(() => { buscar() }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const th: React.CSSProperties = { padding: '0.45rem 0.6rem', fontSize: '0.68rem', fontWeight: 800, color: '#fff', background: '#1b3f7b', textAlign: 'left', whiteSpace: 'nowrap', textTransform: 'uppercase' }
  const td: React.CSSProperties = { padding: '0.4rem 0.6rem', fontSize: '0.82rem', borderBottom: '1px solid #eef2f7', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }

  return (
    <div className="card">
      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: '1rem' }}>
        <div className="field">
          <label>Período</label>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <input type="date" value={inicio} onChange={e => setInicio(e.target.value)} />
            <span style={{ color: '#6b7280', fontSize: '0.8rem' }}>a</span>
            <input type="date" value={fim} onChange={e => setFim(e.target.value)} />
          </div>
        </div>
        <div className="field" style={{ minWidth: 260 }}>
          <label>Funcionário</label>
          <LookupSelect endpoint="/funcionarios" extraParams={{ status: 'todos' }} codKey="motcod" descKey="motnome" tagKey="motcod"
            codValue={funcCod} descValue={funcNome} onSelect={(c, d) => { setFuncCod(c); setFuncNome(d) }} placeholder="Todos" listWidth={320} />
        </div>
        <div className="field">
          <label>Natureza</label>
          <select value={natureza} onChange={e => setNatureza(e.target.value)}>
            <option value="">Todas</option>
            {NATUREZAS.map(([c, n]) => <option key={c} value={c}>{n}</option>)}
          </select>
        </div>
        <div className="field">
          <label>Status</label>
          <select value={status} onChange={e => setStatus(e.target.value)}>
            <option value="">Todos</option>
            <option value="A">Em Andamento</option>
            <option value="C">Concluída</option>
          </select>
        </div>
        <button className="btn btn-primary" onClick={buscar}>Buscar</button>
        {funcCod && <button className="btn btn-outline" onClick={() => { setFuncCod(''); setFuncNome('') }}>Limpar funcionário</button>}
        <div style={{ flex: 1 }} />
        <button className="btn btn-primary" onClick={onNovo}>+ Nova Ocorrência</button>
      </div>

      {erro && <div style={{ color: '#b91c1c', fontSize: '0.85rem', marginBottom: '0.75rem' }}>{erro}</div>}
      {lista && lista.length === 0 && <p style={{ color: '#6b7280', fontSize: '0.9rem' }}>Nenhuma ocorrência no período.</p>}
      {lista && lista.length > 0 && (
        <div style={{ overflowX: 'auto', border: '1px solid #bfdbfe', borderRadius: 8 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: 46 }} /><col style={{ width: 92 }} /><col style={{ width: 72 }} /><col /><col /><col style={{ width: 104 }} />
              <col style={{ width: 74 }} /><col style={{ width: 92 }} /><col style={{ width: 124 }} /><col style={{ width: 92 }} />
            </colgroup>
            <thead>
              <tr>
                <th style={{ ...th, width: 44 }}></th><th style={th}>Data</th><th style={th}>Número</th><th style={th}>Funcionário</th><th style={th}>Tipo</th>
                <th style={th}>Natureza</th><th style={th}>Veículo</th><th style={{ ...th, textAlign: 'right' }}>Valor</th>
                <th style={th}>Status</th><th style={th}>Usuário</th>
              </tr>
            </thead>
            <tbody>
              {lista.map((l, i) => (
                <tr key={`${l.ococod}-${l.ocodat}-${l.ocotip}-${l.oconro}`} style={{ background: i % 2 ? '#f5f9fd' : '#fff', cursor: 'pointer' }}
                  onDoubleClick={() => onEditar(l)} title="Duplo clique para editar">
                  <td style={{ ...td, textAlign: 'center' }}>
                    <button className="row-action-btn row-action-btn--edit" title="Editar ocorrência" onClick={() => onEditar(l)}><Pencil size={15} /></button>
                  </td>
                  <td style={td}>{br(l.ocodat)}</td>
                  <td style={td}>{l.oconro}</td>
                  <td style={{ ...td, fontWeight: 600 }} title={l.nome}>{l.ococod ? `${l.ococod} - ${l.nome ?? ''}` : '—'}</td>
                  <td style={td} title={l.tipo}>{l.ocotip} - {l.tipo}</td>
                  <td style={td}>{nomeNatureza(l.oconat)}</td>
                  <td style={td}>{l.ocovei}</td>
                  <td style={{ ...td, textAlign: 'right' }}>{l.ocoval ? moeda(l.ocoval) : ''}</td>
                  <td style={{ ...td, fontWeight: 600, color: l.ocosta === 'C' ? '#15803d' : '#b45309' }}>{l.ocosta === 'C' ? 'Concluída' : 'Em Andamento'}</td>
                  <td style={td} title={l.ocousu}>{l.ocousu}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {lista && lista.length === 500 && <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: 6 }}>Mostrando as 500 mais recentes — refine o filtro.</div>}
    </div>
  )
}

function Formulario({ inicial, editando, abrirTexto, onVoltar, onSalvo, onLancada }: {
  inicial: Ocorrencia; editando: boolean; abrirTexto?: boolean; onVoltar: () => void; onSalvo: () => void; onLancada: (chave: ChaveOcorrencia) => void
}) {
  const [textoAberto, setTextoAberto] = useState(!!abrirTexto)
  const [relatoAberto, setRelatoAberto] = useState(false)
  const [envolvidosAberto, setEnvolvidosAberto] = useState(false)
  const [emailAberto, setEmailAberto] = useState(false)
  const [imprimindo, setImprimindo] = useState<'' | 'carta' | 'completa'>('')
  const { empresaCod } = useEmpresa()
  const [f, setF] = useState<Ocorrencia>(inicial)
  const [tipos, setTipos] = useState<{ ocrcod: number; ocrnom: string }[]>([])
  const [erro, setErro] = useState('')
  const [salvando, setSalvando] = useState(false)
  const set = <K extends keyof Ocorrencia>(k: K, v: Ocorrencia[K]) => setF(x => ({ ...x, [k]: v }))
  const nat = f.oconat

  useEffect(() => { api.get('/ocorrencias').then(r => setTipos(r.data)).catch(() => setTipos([])) }, [])

  async function salvar() {
    if (!f.ocodat) { setErro('Data Inválida'); return }
    if (!f.ocotip) { setErro('Informe o tipo de ocorrência.'); return }
    setSalvando(true); setErro('')
    let lancada: any = null
    try {
      if (editando) await api.put('/ocorrencias-funcionarios', f)
      else {
        lancada = (await api.post('/ocorrencias-funcionarios', f)).data
        // fotos escolhidas antes de lançar só sobem depois que a ocorrência tem chave
        for (const [tp, imagem] of Object.entries(pendentes)) {
          if (imagem) await api.put('/ocorrencias-funcionarios/foto', { ...lancada, tipo: tp, imagem })
        }
        // igual ao ERP: depois de incluir já abre o Texto da Ocorrência
        onLancada(lancada)
        return
      }
      onSalvo()
    } catch (e: any) {
      const msg = e.response?.data?.error ?? 'Erro ao salvar.'
      setErro(lancada ? `Ocorrência nº ${lancada.oconro} lançada, mas a foto não foi enviada: ${msg}` : msg)
    } finally {
      setSalvando(false)
    }
  }

  // ── Fotos do sinistro (frota.ocomot_foto): na edição sobem na hora; na inclusão ficam pendentes até lançar ──
  const [fotos, setFotos] = useState<Partial<Record<TipoFoto, string>>>({})
  const [pendentes, setPendentes] = useState<Partial<Record<TipoFoto, string>>>({})
  const [enviandoFoto, setEnviandoFoto] = useState<TipoFoto | ''>('')
  const arquivoRef = useRef<Partial<Record<TipoFoto, HTMLInputElement | null>>>({})
  const chave = { ococod: f.ococod, ocodat: f.ocodat, ocotip: f.ocotip, oconro: f.oconro }

  useEffect(() => {
    if (!editando || !inicial.fotos) return
    const urls: string[] = []
    for (const tp of inicial.fotos.split('') as TipoFoto[]) {
      api.get('/ocorrencias-funcionarios/foto', { params: { ...chave, tipo: tp }, responseType: 'blob' })
        .then(r => { const u = URL.createObjectURL(r.data); urls.push(u); setFotos(x => ({ ...x, [tp]: u })) }).catch(() => {})
    }
    return () => urls.forEach(u => URL.revokeObjectURL(u))
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  async function escolherFoto(tp: TipoFoto, arquivo: File) {
    setErro('')
    setEnviandoFoto(tp)
    try {
      const imagem = await reduzirImagem(arquivo)
      if (editando) await api.put('/ocorrencias-funcionarios/foto', { ...chave, tipo: tp, imagem })
      else setPendentes(x => ({ ...x, [tp]: imagem }))
      setFotos(x => ({ ...x, [tp]: imagem }))
    } catch (e: any) {
      setErro(e?.response?.data?.error || e?.message || 'Erro ao enviar a foto.')
    } finally {
      setEnviandoFoto('')
    }
  }

  async function removerFoto(tp: TipoFoto) {
    if (editando) {
      if (!window.confirm('Remover esta foto?')) return
      try { await api.delete('/ocorrencias-funcionarios/foto', { params: { ...chave, tipo: tp } }) }
      catch (e: any) { setErro(e?.response?.data?.error || 'Erro ao remover a foto.'); return }
    }
    setPendentes(x => ({ ...x, [tp]: undefined }))
    setFotos(x => ({ ...x, [tp]: undefined }))
  }

  // PDFs da lateral: carta = Aviso de Advertência (ROCOADV); completa = Relatório da Ocorrência (ROCOGER)
  async function abrirPdf(qual: 'carta' | 'completa') {
    setErro('')
    setImprimindo(qual)
    try {
      const r = await api.get(`/ocorrencias-funcionarios/${qual}`, {
        params: { ococod: f.ococod, ocodat: f.ocodat, ocotip: f.ocotip, oconro: f.oconro, empresa: empresaCod ?? 1 }, responseType: 'blob',
      })
      window.open(URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' })), '_blank')
    } catch (e: any) {
      let msg = qual === 'carta' ? 'Erro ao gerar a advertência.' : 'Erro ao gerar o relatório da ocorrência.'
      try { if (e?.response?.data instanceof Blob) msg = JSON.parse(await e.response.data.text()).error || msg } catch {}
      setErro(msg)
    } finally {
      setImprimindo('')
    }
  }

  async function excluir() {
    if (!window.confirm(`Excluir a ocorrência nº ${f.oconro}? O texto, envolvidos e auditorias dela também serão apagados.`)) return
    setErro('')
    try {
      await api.delete('/ocorrencias-funcionarios', { params: { ococod: f.ococod, ocodat: f.ocodat, ocotip: f.ocotip, oconro: f.oconro } })
      onSalvo()
    } catch (e: any) {
      setErro(e.response?.data?.error ?? 'Erro ao excluir.')
    }
  }

  // foto do funcionário (frota.cadmot_foto), igual ao quadro "Foto Funcionário" do ERP
  const [fotoFunc, setFotoFunc] = useState<string | null>(null)
  useEffect(() => {
    setFotoFunc(null)
    if (!f.ococod) return
    let url = ''
    api.get(`/funcionarios/${encodeURIComponent(f.ococod)}/foto`, { responseType: 'blob' })
      .then(r => { url = URL.createObjectURL(r.data); setFotoFunc(url) }).catch(() => {})
    return () => { if (url) URL.revokeObjectURL(url) }
  }, [f.ococod])

  const grade = (cols: string): React.CSSProperties => ({ display: 'grid', gridTemplateColumns: cols, gap: '0.75rem 1rem', alignItems: 'start' })
  const cheio: React.CSSProperties = { width: '100%', boxSizing: 'border-box' }
  const bloco: React.CSSProperties = { border: '1px solid #c7d6ea', borderRadius: 12, padding: '0.9rem 1rem 1rem', background: '#fff',
    boxShadow: '0 6px 18px -10px rgba(15,42,82,0.35)', overflow: 'hidden' }
  const titulo: React.CSSProperties = { margin: '-0.9rem -1rem 0.9rem', padding: '0.55rem 1rem', display: 'flex', alignItems: 'center', gap: 8,
    background: 'linear-gradient(135deg, #e8f1fb, #d6e6f7)', borderBottom: '1px solid #c7d6ea', borderLeft: '4px solid #1a6ea8',
    fontSize: '0.74rem', fontWeight: 800, color: '#1b3f7b', textTransform: 'uppercase', letterSpacing: '0.05em', alignSelf: 'stretch' }
  const rotulo: React.CSSProperties = { display: 'block', fontSize: '0.75rem', fontWeight: 600, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.35rem' }

  return (
    <div className="modal-box" style={{ width: '100%', maxWidth: 'none', maxHeight: 'none', boxShadow: '0 12px 32px -8px rgba(15,23,42,0.18)' }}>
      <div className="modal-header" style={{ background: 'linear-gradient(135deg, #1a6ea8, #14547f)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.7rem' }}>
          <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 34, height: 34, borderRadius: 9, background: 'rgba(255,255,255,0.15)' }}>
            <AlertTriangle size={18} color="#fff" />
          </span>
          <div>
            <h2>{editando ? 'Ocorrência do Funcionário' : 'Nova Ocorrência'}</h2>
            <div style={{ fontSize: '0.7rem', color: '#dbeafe', marginTop: 2 }}>Lançamento de ocorrências, advertências e sinistros</div>
          </div>
          <span title={editando ? 'Número da ocorrência' : 'O número é gerado ao lançar'}
            style={{ marginLeft: 8, padding: '5px 14px', borderRadius: 8, background: editando ? '#fff' : 'rgba(255,255,255,0.15)',
              color: editando ? '#1b3f7b' : '#dbeafe', border: '1px solid rgba(255,255,255,0.35)',
              fontWeight: 800, fontSize: editando ? '1.05rem' : '0.78rem', letterSpacing: '0.02em', userSelect: 'all' }}>
            {editando ? `Nº ${f.oconro}` : 'Nº gerado ao lançar'}
          </span>
        </div>
        <button className="btn" style={{ background: 'rgba(255,255,255,0.15)', color: '#fff', border: '1px solid rgba(255,255,255,0.35)' }} onClick={onVoltar}>Voltar</button>
      </div>

      <div className="modal-corpo" style={{ background: '#f1f5fb' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 200px', gap: '1rem', alignItems: 'start' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.9rem', minWidth: 0 }}>
          <div style={bloco}>
            <div style={titulo}><ClipboardList size={15} /> Ocorrência</div>
            <div style={grade('160px minmax(0, 1fr) 170px')}>
              <div className="field" style={{ margin: 0 }}>
                <label>Data da Ocorrência *</label>
                <input type="date" value={f.ocodat} disabled={editando} style={cheio} onChange={e => set('ocodat', e.target.value)} />
              </div>
              <div className="field" style={{ margin: 0, minWidth: 0 }}>
                <label>Funcionário</label>
                {editando
                  ? <input value={f.ococod ? `${f.ococod} - ${f.nome ?? ''}` : '—'} disabled style={cheio} />
                  : <LookupSelect endpoint="/funcionarios" codKey="motcod" descKey="motnome" tagKey="motcod" codValue={f.ococod} descValue={f.nome}
                      onSelect={(c, d) => setF(x => ({ ...x, ococod: c, nome: d }))} placeholder="Buscar funcionário..." listWidth={360} />}
              </div>
              <div className="field" style={{ margin: 0 }}>
                <label>Status</label>
                <select value={f.ocosta} style={cheio} onChange={e => set('ocosta', e.target.value)}>
                  <option value="A">Em Andamento</option>
                  <option value="C">Concluída</option>
                </select>
              </div>
            </div>
            <div style={{ ...grade('minmax(0, 1fr) 170px'), marginTop: '0.75rem' }}>
              <div className="field" style={{ margin: 0, minWidth: 0 }}>
                <label>Tipo de Ocorrência *</label>
                <select value={f.ocotip || ''} disabled={editando} style={cheio} onChange={e => set('ocotip', Number(e.target.value))}>
                  <option value="">Selecione...</option>
                  {tipos.map(t => <option key={t.ocrcod} value={t.ocrcod}>{t.ocrcod} - {t.ocrnom}</option>)}
                </select>
              </div>
              <div className="field" style={{ margin: 0 }}>
                <label>Natureza *</label>
                <select value={nat} style={cheio} onChange={e => set('oconat', e.target.value)}>
                  {NATUREZAS.map(([c, n]) => <option key={c} value={c}>{n}</option>)}
                </select>
              </div>
            </div>
          </div>

          <div style={bloco}>
            <div style={titulo}><FileText size={15} /> Detalhes</div>
            <div style={grade('repeat(3, minmax(0, 1fr))')}>
              <div className="field" style={{ margin: 0 }}>
                <label>Veículo</label>
                <LookupSelect endpoint="/erp/veiculos" codKey="veicod" descKey="veipla" tagKey="veicod" codValue={f.ocovei} descValue={f.ocovei}
                  onSelect={c => set('ocovei', c)} placeholder="—" disabled={!comVeiculo(nat)} />
              </div>
              <div className="field" style={{ margin: 0 }}>
                <label>Número Documento</label>
                <input value={f.ocomul} maxLength={13} disabled={!comMulta(nat)} style={cheio} onChange={e => set('ocomul', e.target.value)} />
              </div>
              <div className="field" style={{ margin: 0 }}>
                <label>Valor Ocorrência</label>
                <input type="number" step="0.01" min={0} value={f.ocoval || ''} disabled={!comMulta(nat)} style={cheio}
                  onChange={e => set('ocoval', Number(e.target.value))} />
              </div>
              <div className="field" style={{ margin: 0 }}>
                <label>Data de Pagamento</label>
                <input type="date" value={f.ocopgt} disabled={!comMulta(nat)} style={cheio} onChange={e => set('ocopgt', e.target.value)} />
              </div>
              <div className="field" style={{ margin: 0 }}>
                <label>Dias da Ocorrência</label>
                <input type="number" min={0} max={999} value={f.ocodia || ''} disabled={!comDias(nat)} style={cheio}
                  onChange={e => set('ocodia', Number(e.target.value))} />
              </div>
              <div className="field" style={{ margin: 0 }}>
                <label>Data Vencimento</label>
                <input value={comDias(nat) && f.ocodia > 0 ? br(somaDias(f.ocodat, f.ocodia - 1)) : ''} disabled
                  style={{ ...cheio, color: '#b91c1c', fontWeight: 600 }} />
              </div>
            </div>
            <div style={{ ...grade('repeat(2, minmax(0, 1fr))'), marginTop: '0.75rem' }}>
              {(['ocots1', 'ocots2'] as const).map((k, i) => {
                const nk = k === 'ocots1' ? 'ts1nome' : 'ts2nome'
                return (
                  <div key={k} className="field" style={{ margin: 0, minWidth: 0 }}>
                    <label>Testemunha {i + 1}</label>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <LookupSelect endpoint="/funcionarios" codKey="motcod" descKey="motnome" tagKey="motcod" codValue={f[k]} descValue={f[nk]}
                          onSelect={(c, d) => setF(x => ({ ...x, [k]: c, [nk]: d }))} placeholder="—" listWidth={320} />
                      </div>
                      {f[k] && <button className="btn btn-outline btn-sm" title="Limpar" onClick={() => setF(x => ({ ...x, [k]: '', [nk]: '' }))}>✕</button>}
                    </div>
                  </div>
                )
              })}
            </div>
            <div className="field" style={{ margin: '0.75rem 0 0' }}>
              <label>Observação</label>
              <textarea value={f.ocoobs} maxLength={2000} rows={4} style={{ ...cheio, resize: 'vertical' }} onChange={e => set('ocoobs', e.target.value)} />
            </div>
          </div>

          <div style={bloco}>
            <div style={titulo}><ShieldAlert size={15} /> Dados do sinistro</div>
            <div style={grade('repeat(3, minmax(0, 1fr))')}>
              <div className="field" style={{ margin: 0 }}>
                <label>Delegacia</label>
                <input value={f.ocodel} maxLength={40} style={cheio} onChange={e => set('ocodel', e.target.value)} />
              </div>
              <div className="field" style={{ margin: 0 }}>
                <label>Nome Vistoriador</label>
                <input value={f.ocovnm} maxLength={50} style={cheio} onChange={e => set('ocovnm', e.target.value)} />
              </div>
              <div className="field" style={{ margin: 0 }}>
                <label>Ressarcido por</label>
                <input value={f.ocorsc} maxLength={50} style={cheio} onChange={e => set('ocorsc', e.target.value)} />
              </div>
              <div className="field" style={{ margin: 0 }}>
                <label>Fone</label>
                <input value={f.ocovte === '0' ? '' : f.ocovte} maxLength={10} inputMode="numeric" style={cheio}
                  onChange={e => set('ocovte', e.target.value.replace(/\D/g, ''))} />
              </div>
              <div className="field" style={{ margin: 0 }}>
                <label>Hora</label>
                <input type="time" value={horaParaInput(f.ocohor)} style={cheio} onChange={e => set('ocohor', inputParaHora(e.target.value))} />
              </div>
              <div className="field" style={{ margin: 0 }}>
                <label>Custo</label>
                <input type="number" step="0.01" min={0} value={f.ococus || ''} style={cheio} onChange={e => set('ococus', Number(e.target.value))} />
              </div>
            </div>
            <div style={{ ...grade('repeat(2, minmax(0, 1fr))'), marginTop: '0.9rem', alignItems: 'start' }}>
              {TIPOS_FOTO.map(([tp, rot]) => (
                <div key={tp}>
                  <span style={rotulo}><Camera size={13} style={{ verticalAlign: -2, marginRight: 4 }} />{rot}</span>
                  <input ref={el => { arquivoRef.current[tp] = el }} type="file" accept="image/jpeg,image/png,image/webp" style={{ display: 'none' }}
                    onChange={e => { const a = e.target.files?.[0]; e.target.value = ''; if (a) escolherFoto(tp, a) }} />
                  {fotos[tp]
                    ? <a href={fotos[tp]!} target="_blank" rel="noreferrer" title="Abrir em tamanho real"
                        style={{ display: 'block', height: 170, borderRadius: 10, border: '1px solid #c7d6ea', overflow: 'hidden', background: '#fff' }}>
                        <img src={fotos[tp]!} alt={rot} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                      </a>
                    : <button type="button" onClick={() => arquivoRef.current[tp]?.click()} disabled={enviandoFoto === tp}
                        style={{ width: '100%', height: 170, borderRadius: 10, border: '1.5px dashed #9fbbe0', background: '#f5f9fe', cursor: 'pointer',
                          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6, color: '#1a6ea8', fontSize: '0.8rem', fontWeight: 600 }}>
                        <Camera size={26} />
                        {enviandoFoto === tp ? 'Enviando...' : 'Clique para adicionar a foto'}
                      </button>}
                  <div style={{ display: 'flex', gap: 6, marginTop: 8, alignItems: 'center' }}>
                    <button type="button" className="btn btn-primary btn-sm" style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}
                      onClick={() => arquivoRef.current[tp]?.click()} disabled={enviandoFoto === tp}>
                      <Upload size={14} /> {fotos[tp] ? 'Trocar foto' : 'Adicionar foto'}
                    </button>
                    {fotos[tp] && (
                      <button type="button" className="btn btn-sm" style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: '#fff', color: '#b91c1c', border: '1px solid #fecaca' }}
                        onClick={() => removerFoto(tp)}>
                        <Trash2 size={14} /> Remover
                      </button>
                    )}
                    {!editando && pendentes[tp] && <span style={{ fontSize: '0.72rem', color: '#6b7280' }}>enviada ao lançar</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div style={{ ...bloco, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
          <div style={titulo}><UserRound size={15} /> Foto Funcionário</div>
          <div style={{ width: 150, height: 180, borderRadius: 8, border: '1px solid #e5e7eb', background: '#f8fafc', overflow: 'hidden',
            display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: '0.78rem', textAlign: 'center' }}>
            {fotoFunc ? <img src={fotoFunc} alt="Foto do funcionário" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              : f.ococod ? 'Sem foto' : 'Selecione o funcionário'}
          </div>
          {f.ococod && <div style={{ fontSize: '0.75rem', fontWeight: 600, textAlign: 'center', color: '#374151' }}>{f.nome}</div>}

          <div style={{ ...titulo, margin: '0.6rem -1rem 0.2rem', borderTop: '1px solid #c7d6ea' }}><ClipboardList size={15} /> Cadastros</div>
          <button type="button" className="btn btn-primary" disabled={!editando} onClick={() => setTextoAberto(true)}
            title={editando ? 'Texto da Ocorrência (TOCOTEX)' : 'Disponível depois de lançar a ocorrência'}
            style={{ width: '100%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, fontSize: '0.8rem' }}>
            <FileText size={15} /> Texto da Ocorrência
          </button>
          <button type="button" className="btn btn-primary" disabled={!editando} onClick={() => setRelatoAberto(true)}
            title={editando ? 'Relatório do Funcionário (TMOTOC1)' : 'Disponível depois de lançar a ocorrência'}
            style={{ width: '100%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, fontSize: '0.8rem' }}>
            <MessageSquareText size={15} /> Relat. Funcionário
          </button>
          <button type="button" className="btn btn-primary" disabled={!editando} onClick={() => setEnvolvidosAberto(true)}
            title={editando ? 'Sinistro — envolvidos (TOCOEVV)' : 'Disponível depois de lançar a ocorrência'}
            style={{ width: '100%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, fontSize: '0.8rem' }}>
            <Users size={15} /> Sinistro
          </button>
          <button type="button" className="btn btn-primary" disabled={!editando} onClick={() => setEmailAberto(true)}
            title={editando ? 'Enviar e-mail da ocorrência (WOCOEMA)' : 'Disponível depois de lançar a ocorrência'}
            style={{ width: '100%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, fontSize: '0.8rem' }}>
            <Mail size={15} /> E-mail
          </button>

          <div style={{ ...titulo, margin: '0.6rem -1rem 0.2rem', borderTop: '1px solid #c7d6ea' }}><Printer size={15} /> Relatórios</div>
          <button type="button" className="btn btn-primary" disabled={!editando || inicial.oconat !== 'A' || !!imprimindo} onClick={() => abrirPdf('carta')}
            title={!editando ? 'Disponível depois de lançar a ocorrência' : inicial.oconat !== 'A' ? 'Só para ocorrências de natureza Advertência' : 'Aviso de Advertência ao Funcionário (2 vias)'}
            style={{ width: '100%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, fontSize: '0.8rem' }}>
            <Printer size={15} /> {imprimindo === 'carta' ? 'Gerando...' : 'Imprimir Advertência'}
          </button>
          <button type="button" className="btn btn-primary" disabled={!editando || !!imprimindo} onClick={() => abrirPdf('completa')}
            title={editando ? 'Relatório da ocorrência com texto, sinistro, envolvidos e auditorias' : 'Disponível depois de lançar a ocorrência'}
            style={{ width: '100%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, fontSize: '0.8rem' }}>
            <FileStack size={15} /> {imprimindo === 'completa' ? 'Gerando...' : 'Ocorrência Completa'}
          </button>
          {!editando && <div style={{ fontSize: '0.7rem', color: '#6b7280', textAlign: 'center' }}>Liberado depois de lançar</div>}
        </div>
      </div>

      {textoAberto && (
        <OcorrenciaTextoModal chave={{ ococod: f.ococod, ocodat: f.ocodat, ocotip: f.ocotip, oconro: f.oconro }}
          funcionario={f.ococod ? `${f.ococod} - ${f.nome ?? ''}` : ''}
          tipo={`${f.ocotip} - ${f.tipo ?? tipos.find(t => t.ocrcod === f.ocotip)?.ocrnom ?? ''}`}
          onClose={() => setTextoAberto(false)} />
      )}
      {relatoAberto && (
        <OcorrenciaRelatoModal chave={{ ococod: f.ococod, ocodat: f.ocodat, ocotip: f.ocotip, oconro: f.oconro }}
          funcionario={f.ococod ? `${f.ococod} - ${f.nome ?? ''}` : ''}
          tipo={`${f.ocotip} - ${f.tipo ?? tipos.find(t => t.ocrcod === f.ocotip)?.ocrnom ?? ''}`}
          onClose={() => setRelatoAberto(false)} />
      )}
      {envolvidosAberto && (
        <OcorrenciaEnvolvidosModal chave={{ ococod: f.ococod, ocodat: f.ocodat, ocotip: f.ocotip, oconro: f.oconro }}
          funcionario={f.ococod ? `${f.ococod} - ${f.nome ?? ''}` : ''}
          tipo={`${f.ocotip} - ${f.tipo ?? tipos.find(t => t.ocrcod === f.ocotip)?.ocrnom ?? ''}`}
          onClose={() => setEnvolvidosAberto(false)} />
      )}
      {emailAberto && (
        <OcorrenciaEmailModal chave={{ ococod: f.ococod, ocodat: f.ocodat, ocotip: f.ocotip, oconro: f.oconro }}
          funcionario={f.ococod ? `${f.ococod} - ${f.nome ?? ''}` : ''}
          tipo={`${f.ocotip} - ${f.tipo ?? tipos.find(t => t.ocrcod === f.ocotip)?.ocrnom ?? ''}`}
          onClose={() => setEmailAberto(false)} />
      )}

      {erro && (
        <div style={{ marginTop: '0.9rem', fontSize: '0.8rem', fontWeight: 600, padding: '0.55rem 0.9rem', borderRadius: 8, background: '#fee2e2', color: '#991b1b', border: '1px solid #fecaca' }}>
          {erro}
        </div>
      )}
      </div>

      <div className="modal-footer">
        {editando && <button className="btn" style={{ background: '#fff', color: '#b91c1c', border: '1px solid #fecaca', marginRight: 'auto' }} onClick={excluir}>Excluir</button>}
        <button className="btn btn-outline" onClick={onVoltar}>Cancelar</button>
        <button className="btn btn-primary" onClick={salvar} disabled={salvando}>{salvando ? 'Salvando...' : editando ? 'Salvar alterações' : 'Lançar'}</button>
      </div>
    </div>
  )
}

export default function LancamentoOcorrencias() {
  const [atual, setAtual] = useState<Ocorrencia | null>(null)
  const [editando, setEditando] = useState(false)
  const [erro, setErro] = useState('')
  const [versao, setVersao] = useState(0)
  const [abrirTexto, setAbrirTexto] = useState(false)

  async function abrir(l: ChaveOcorrencia, comTexto = false) {
    setErro('')
    try {
      const r = await api.get('/ocorrencias-funcionarios/item', { params: { ococod: l.ococod, ocodat: l.ocodat, ocotip: l.ocotip, oconro: l.oconro } })
      setAbrirTexto(comTexto)
      setAtual({ ...r.data, ocovte: r.data.ocovte ?? '' })
      setEditando(true)
      setVersao(v => v + 1)
    } catch (e: any) {
      setErro(e.response?.data?.error ?? 'Erro ao abrir a ocorrência.')
    }
  }

  return (
    <div style={{ width: '100%', maxWidth: 1200 }}>
      <h2 style={{ marginTop: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
        Lançamento de Ocorrências
        <span style={{ color: '#9ca3af', fontFamily: 'monospace', fontSize: '0.75rem', fontWeight: 400 }} title="Tabela frota.ocomot">TMOTOCO</span>
      </h2>
      {erro && <div style={{ color: '#b91c1c', fontSize: '0.85rem', marginBottom: '0.75rem' }}>{erro}</div>}
      {atual ? (
        <Formulario key={`${atual.oconro}-${versao}`} inicial={atual} editando={editando} abrirTexto={abrirTexto}
          onVoltar={() => setAtual(null)} onSalvo={() => { setAtual(null); setVersao(v => v + 1) }}
          onLancada={chave => abrir(chave, true)} />
      ) : (
        <Lista key={versao} onNovo={() => { setAtual(novo()); setEditando(false) }} onEditar={abrir} />
      )}
    </div>
  )
}
